import React, { useEffect, useState } from 'react';
import { Card, Spinner, Badge } from 'react-bootstrap';
import ApiService from '../../helpers/apiService';

interface QuestionnaireCardProps {
  userId: string;
  /** When false the card is rendered collapsed (just the header). Default: true. */
  defaultOpen?: boolean;
}

interface QuestionnaireItem {
  value?: string;
  values?: string[];
}

interface QuestionnaireSection {
  items?: Record<string, QuestionnaireItem>;
}

interface QuestionnaireEntry {
  id?: string;
  sections?: Record<string, QuestionnaireSection>;
}

type RenderStrategy = 'fields' | 'address' | 'chips' | 'pep' | 'agreement';

const SECTION_LABELS: Record<string, string> = {
  personalInformation: 'Личная информация',
  documentDetails: 'Документ',
  placeOfRegistration: 'Место регистрации',
  taxResidency: 'Налоговое резидентство',
  governmentWorkers: 'Госслужащий / PEP',
  sourcesOfIncome: 'Источники дохода',
  purposesOfUsingTesla: 'Цели использования сервиса',
  plannedMonthlyPaymen: 'Планируемый месячный оборот',
  serviceUseAgreement: 'Согласие с правилами',
  personal: 'Личная информация',
  contact: 'Контакты',
};

const FIELD_LABELS: Record<string, string> = {
  // — Personal —
  firstName: 'Имя',
  firstNameEn: 'Имя (лат.)',
  lastName: 'Фамилия',
  lastNameEn: 'Фамилия (лат.)',
  middleName: 'Отчество',
  middleNameEn: 'Отчество (лат.)',
  dateOfBirth: 'Дата рождения',
  dob: 'Дата рождения',
  gender: 'Пол',
  nationality: 'Гражданство',
  placeCountryOfBirth: 'Страна рождения',
  personalIdentityCode: 'Идент. код',
  phone: 'Телефон',
  email: 'Email',
  // — Address —
  country: 'Страна',
  zipCode: 'Индекс',
  postCode: 'Индекс',
  cityregion: 'Город / регион',
  town: 'Город',
  townEn: 'Город (лат.)',
  state: 'Регион',
  stateEn: 'Регион (лат.)',
  street: 'Улица',
  streetEn: 'Улица (лат.)',
  subStreet: 'Доп. адрес',
  subStreetEn: 'Доп. адрес (лат.)',
  house: 'Дом',
  buildingNumber: 'Дом',
  buildingName: 'Здание',
  apartment: 'Квартира',
  flatNumber: 'Квартира',
  formattedAddress: 'Адрес',
  // — Document —
  number: 'Номер документа',
  documentType: 'Тип документа',
  idDocType: 'Тип документа',
  validityDate: 'Действителен до',
  issueDate: 'Дата выдачи',
  // — Tax —
  tin: 'TIN / ИНН',
  countryOfTaxAgent: 'Страна налогового агента',
};

// Какие поля считаются «ключевыми» для секции — value рендерится `fw-semibold`
const KEY_FIELDS = new Set([
  'firstName', 'lastName', 'middleName',
  'documentType', 'number',
  'tin', 'formattedAddress',
]);

// Поля-коды → font-monospace
const MONO_FIELDS = new Set([
  'personalIdentityCode', 'tin', 'number', 'zipCode', 'postCode',
]);

// Поля-даты → font-monospace для табличного выравнивания
const DATE_FIELDS = new Set([
  'dateOfBirth', 'dob', 'validityDate', 'issueDate',
]);

const SECTION_ORDER: string[] = [
  'personalInformation',
  'documentDetails',
  'placeOfRegistration',
  'taxResidency',
  'governmentWorkers',
  'sourcesOfIncome',
  'purposesOfUsingTesla',
  'plannedMonthlyPaymen',
  'serviceUseAgreement',
];

const SECTION_GROUPS: { label: string; sections: string[] }[] = [
  { label: 'Личность и документ', sections: ['personalInformation', 'documentDetails', 'placeOfRegistration', 'taxResidency'] },
  { label: 'Профиль использования', sections: ['governmentWorkers', 'sourcesOfIncome', 'purposesOfUsingTesla', 'plannedMonthlyPaymen', 'serviceUseAgreement'] },
];

const SECTION_RENDER: Record<string, RenderStrategy> = {
  personalInformation: 'fields',
  documentDetails: 'fields',
  placeOfRegistration: 'address',
  taxResidency: 'fields',
  governmentWorkers: 'pep',
  sourcesOfIncome: 'chips',
  purposesOfUsingTesla: 'chips',
  plannedMonthlyPaymen: 'chips',
  serviceUseAgreement: 'agreement',
};

const labelStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  letterSpacing: '0.04em',
  fontWeight: 500,
  color: '#6c757d',
};
const valueStyle: React.CSSProperties = { fontSize: '0.875rem' };
const sectionHeaderStyle: React.CSSProperties = {
  fontSize: '0.78rem',
  letterSpacing: '0.04em',
  color: '#495057',
};
const groupHeaderStyle: React.CSSProperties = {
  fontSize: '0.82rem',
  letterSpacing: '0.06em',
  color: '#212529',
};

const valueOf = (item?: QuestionnaireItem): string => {
  if (!item) return '';
  if (item.value) return String(item.value).trim();
  return '';
};

const valuesOf = (item?: QuestionnaireItem): string[] => {
  if (!item) return [];
  if (Array.isArray(item.values)) return item.values.filter(Boolean);
  if (item.value) return [item.value];
  return [];
};

const FieldPair = ({ k, value }: { k: string; value: string }) => {
  const isKey = KEY_FIELDS.has(k);
  const isMono = MONO_FIELDS.has(k) || DATE_FIELDS.has(k);
  const labelText = FIELD_LABELS[k] || k;
  // formattedAddress / адрес одной строкой занимает всю строку
  const colCls = k === 'formattedAddress' ? 'col-12' : 'col-12 col-md-6';
  return (
    <div className={colCls}>
      <div className="text-uppercase mb-1" style={labelStyle}>{labelText}</div>
      <div
        className={`${isKey ? 'fw-semibold' : ''} ${isMono ? 'font-monospace' : ''}`}
        style={valueStyle}
      >
        {value}
      </div>
    </div>
  );
};

const SectionHeader = ({ title, filled, total }: { title: string; filled: number; total?: number }) => {
  const allFilled = total !== undefined && filled === total && total > 0;
  const partial = total !== undefined && filled < total && filled > 0;
  let counter: React.ReactNode = null;
  if (total !== undefined) {
    if (allFilled) {
      counter = <Badge bg="success">{filled} / {total} ✓</Badge>;
    } else if (partial) {
      counter = <Badge bg="warning" text="dark">{filled} / {total}</Badge>;
    } else if (filled > 0) {
      counter = <Badge bg="light" text="dark" className="border">{filled}</Badge>;
    }
  } else if (filled > 0) {
    counter = <Badge bg="light" text="dark" className="border">{filled}</Badge>;
  }
  return (
    <div className="d-flex justify-content-between align-items-center pb-1 mb-2 border-bottom">
      <span className="text-uppercase fw-semibold" style={sectionHeaderStyle}>{title}</span>
      {counter}
    </div>
  );
};

// Plain field-grid: набор label/value пар.
const renderFieldsSection = (
  sectionKey: string,
  section: QuestionnaireSection,
  expectedFields?: string[]
) => {
  const items = section?.items || {};
  const rows: { key: string; value: string }[] = [];
  const seen = new Set<string>();
  const pushField = (k: string) => {
    if (seen.has(k)) return;
    seen.add(k);
    const v = valueOf(items[k]);
    if (v) rows.push({ key: k, value: v });
  };
  // Сначала expectedFields в нужном порядке, потом остальные
  (expectedFields ?? []).forEach(pushField);
  Object.keys(items).forEach(pushField);

  if (rows.length === 0) return null;
  const total = expectedFields?.length;
  const filled = expectedFields
    ? expectedFields.filter(k => Boolean(valueOf(items[k]))).length
    : rows.length;
  return (
    <div key={sectionKey} className="mb-3">
      <SectionHeader title={SECTION_LABELS[sectionKey] || sectionKey} filled={filled} total={total} />
      <div className="row g-2">
        {rows.map(({ key, value }) => (
          <FieldPair key={key} k={key} value={value} />
        ))}
      </div>
    </div>
  );
};

// Address: предпочитаем formattedAddress одной строкой, иначе раскладываем
const renderAddressSection = (sectionKey: string, section: QuestionnaireSection) => {
  const items = section?.items || {};
  const formatted = valueOf(items.formattedAddress);
  if (formatted) {
    return (
      <div key={sectionKey} className="mb-3">
        <SectionHeader title={SECTION_LABELS[sectionKey] || sectionKey} filled={1} />
        <div className="row g-2">
          <FieldPair k="formattedAddress" value={formatted} />
        </div>
      </div>
    );
  }
  return renderFieldsSection(sectionKey, section, [
    'country', 'state', 'cityregion', 'town', 'zipCode', 'postCode',
    'street', 'subStreet', 'house', 'buildingNumber', 'apartment', 'flatNumber',
  ]);
};

// Chips: section.items.question1.values (или один value) → бейджи
const renderChipsSection = (sectionKey: string, section: QuestionnaireSection) => {
  const items = section?.items || {};
  // values могут быть в любом item — собираем со всех
  const chips: string[] = [];
  Object.values(items).forEach(item => {
    chips.push(...valuesOf(item));
  });
  if (chips.length === 0) return null;
  return (
    <div key={sectionKey} className="mb-3">
      <SectionHeader title={SECTION_LABELS[sectionKey] || sectionKey} filled={chips.length} />
      <div className="d-flex flex-wrap gap-1">
        {chips.map((c, i) => (
          <Badge key={`${c}-${i}`} bg="light" text="dark" className="border" style={{ fontWeight: 400, fontSize: '0.78rem' }}>
            {c}
          </Badge>
        ))}
      </div>
    </div>
  );
};

const isYes = (v: string) => /^(y(es)?|true|да|1)$/i.test(v.trim());
const isNo = (v: string) => /^(n(o)?|false|нет|0)$/i.test(v.trim());

// PEP: специальная отрисовка governmentWorkers
const renderPepSection = (sectionKey: string, section: QuestionnaireSection) => {
  const items = section?.items || {};
  const raw = valueOf(items.question1) || valuesOf(items.question1)[0] || '';
  let badge: React.ReactNode;
  if (isYes(raw)) badge = <Badge bg="danger" style={{ fontSize: '0.82rem' }}>PEP — Да</Badge>;
  else if (isNo(raw)) badge = <Badge bg="success" style={{ fontSize: '0.82rem' }}>Не PEP</Badge>;
  else if (raw) badge = <Badge bg="info" style={{ fontSize: '0.82rem' }}>{raw}</Badge>;
  else badge = <Badge bg="warning" text="dark" style={{ fontSize: '0.82rem' }}>Не указано</Badge>;
  return (
    <div key={sectionKey} className="mb-3">
      <SectionHeader title={SECTION_LABELS[sectionKey] || sectionKey} filled={raw ? 1 : 0} total={1} />
      <div>{badge}</div>
    </div>
  );
};

// Agreement: serviceUseAgreement — bool / multi-checkbox → один Badge
const renderAgreementSection = (sectionKey: string, section: QuestionnaireSection) => {
  const items = section?.items || {};
  const values: string[] = [];
  Object.values(items).forEach(item => values.push(...valuesOf(item)));
  const accepted = values.length > 0 && values.some(v => isYes(v) || /agree|accept|consent|принима/i.test(v));
  const rejected = values.length > 0 && values.every(v => isNo(v));
  let badge: React.ReactNode;
  if (accepted) badge = <Badge bg="success" style={{ fontSize: '0.82rem' }}>✓ Принято</Badge>;
  else if (rejected) badge = <Badge bg="danger" style={{ fontSize: '0.82rem' }}>✗ Не принято</Badge>;
  else if (values.length === 0) return null;
  else badge = <Badge bg="light" text="dark" className="border" style={{ fontSize: '0.82rem' }}>{values.join(', ')}</Badge>;
  return (
    <div key={sectionKey} className="mb-3">
      <SectionHeader title={SECTION_LABELS[sectionKey] || sectionKey} filled={values.length} />
      <div>{badge}</div>
    </div>
  );
};

const SECTION_EXPECTED_FIELDS: Record<string, string[]> = {
  personalInformation: ['firstName', 'lastName', 'middleName', 'dateOfBirth', 'nationality', 'placeCountryOfBirth', 'personalIdentityCode'],
  documentDetails: ['documentType', 'number', 'issueDate', 'validityDate'],
  taxResidency: ['tin', 'countryOfTaxAgent'],
};

const renderSection = (sectionKey: string, section: QuestionnaireSection): React.ReactNode => {
  if (!section) return null;
  const strategy = SECTION_RENDER[sectionKey] ?? 'fields';
  switch (strategy) {
    case 'address':
      return renderAddressSection(sectionKey, section);
    case 'chips':
      return renderChipsSection(sectionKey, section);
    case 'pep':
      return renderPepSection(sectionKey, section);
    case 'agreement':
      return renderAgreementSection(sectionKey, section);
    case 'fields':
    default:
      return renderFieldsSection(sectionKey, section, SECTION_EXPECTED_FIELDS[sectionKey]);
  }
};

// Сортируем секции по SECTION_ORDER, остальные — в хвост в алфавитном порядке
const orderedSectionKeys = (sections: Record<string, QuestionnaireSection>): string[] => {
  const present = new Set(Object.keys(sections));
  const ordered: string[] = [];
  SECTION_ORDER.forEach(k => { if (present.has(k)) ordered.push(k); });
  Object.keys(sections).sort().forEach(k => {
    if (!ordered.includes(k)) ordered.push(k);
  });
  return ordered;
};

const QuestionnaireCard: React.FC<QuestionnaireCardProps> = ({ userId, defaultOpen = true }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [questionnaire, setQuestionnaire] = useState<QuestionnaireEntry[] | null>(null);
  const [open, setOpen] = useState(defaultOpen);

  useEffect(() => {
    if (!userId || !open || questionnaire !== null) return;
    setLoading(true);
    setError(null);
    ApiService.kycGetQuestionnaire(userId)
      .then((res: any) => {
        const q = res?.data?.questionnaire;
        if (Array.isArray(q)) setQuestionnaire(q as QuestionnaireEntry[]);
        else if (q && typeof q === 'object') setQuestionnaire([q as QuestionnaireEntry]);
        else setQuestionnaire([]);
      })
      .catch((err: any) => {
        setError(err?.response?.data?.message || err?.message || 'Не удалось загрузить опросник');
      })
      .finally(() => setLoading(false));
  }, [userId, open, questionnaire]);

  const isEmpty = questionnaire !== null && questionnaire.length === 0;
  const hasData = questionnaire !== null && questionnaire.length > 0;

  return (
    <Card className="mb-3">
      <Card.Header
        className="d-flex justify-content-between align-items-center"
        style={{ cursor: 'pointer' }}
        onClick={() => setOpen(o => !o)}
      >
        <span className="fw-semibold">
          Опросник KYC
          {hasData && <Badge bg="info" className="ms-2">SumSub</Badge>}
          {isEmpty && <Badge bg="secondary" className="ms-2">нет данных</Badge>}
        </span>
        <span className="text-muted small">{open ? '▼' : '▶'}</span>
      </Card.Header>
      {open && (
        <Card.Body>
          {loading && (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" />
            </div>
          )}
          {error && <div className="text-danger small">{error}</div>}
          {!loading && !error && isEmpty && (
            <div className="text-muted small">
              Пользователь не заполнял опросник в SDK.
            </div>
          )}
          {!loading && !error && hasData && questionnaire?.map((entry, idx) => {
            const sections = entry?.sections || {};
            const ordered = orderedSectionKeys(sections);
            if (ordered.length === 0) return null;
            // Группируем по SECTION_GROUPS; неизвестные секции — в "прочее"
            const grouped: { label: string; keys: string[] }[] = SECTION_GROUPS.map(g => ({
              label: g.label,
              keys: g.sections.filter(k => ordered.includes(k)),
            }));
            const known = new Set(SECTION_GROUPS.flatMap(g => g.sections));
            const other = ordered.filter(k => !known.has(k));
            if (other.length > 0) grouped.push({ label: 'Прочее', keys: other });

            return (
              <div key={entry.id || idx}>
                {grouped.map((g, gi) => {
                  if (g.keys.length === 0) return null;
                  const isLast = gi === grouped.filter(x => x.keys.length > 0).length - 1;
                  return (
                    <div key={g.label} className={isLast ? '' : 'mb-2'}>
                      <div className="text-uppercase fw-bold mb-2" style={groupHeaderStyle}>
                        {g.label}
                      </div>
                      {g.keys.map(k => renderSection(k, sections[k]))}
                      {!isLast && <hr className="my-3" />}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </Card.Body>
      )}
    </Card>
  );
};

export default QuestionnaireCard;
